import { PDF_TEMPLATE_KIND, build_combined_template_records, build_table_row, paginate_template_records } from "./report_template_library_helpers";

export default {
  display_title() {
    return this.translate_label(this.title);
  },
  display_subtitle() {
    return this.translate_label(this.subtitle);
  },
  summary_cards() {
    const cards = [
      {
        label: this.translate_label("Templates"),
        value: this.total_count || 0,
        description: this.translate_label(
          "Reusable data-report and PDF-report setups available for this project."
        ),
      },
      {
        label: this.translate_label("Data reports"),
        value: this.standard_templates.length,
        description: this.translate_label(
          "Column-based report templates that use the selected output format."
        ),
      },
      {
        label: this.translate_label("PDF reports"),
        value: this.pdf_templates.length,
        description: this.translate_label(
          "Branded single-entity PDF templates that sit in the same library."
        ),
      },
    ];
    return this.include_pdf_templates ? cards : cards.slice(0, 2);
  },
  template_table_headers() {
    return [
      "ID",
      "Template Name",
      "Template Type",
      "Category",
      "Scope",
      "Entity Types",
      "Setup",
      "Updated",
    ].map((label) => this.translate_label(label));
  },
  detail_column_headers() {
    return ["Order", "Field", "Display Label", "Include Blank"].map(
      (label) => this.translate_label(label)
    );
  },
  detail_question_filter_headers() {
    return [
      "Filter Type",
      "Question",
      "Question Type",
      "Form",
      "Section",
      "Value",
    ].map((label) => this.translate_label(label));
  },
  detail_question_metric_headers() {
    return [
      "Order",
      "Metric",
      "Question",
      "Question Type",
      "Form",
      "Section",
    ].map((label) => this.translate_label(label));
  },
  detail_other_filter_headers() {
    return [
      "Filter Type",
      "Form",
      "Section",
      "Compliance Rule",
      "Value",
    ].map((label) => this.translate_label(label));
  },
  detail_entity_type_headers() {
    return ["ID", "Entity Type"].map((label) => this.translate_label(label));
  },
  detail_pdf_section_headers() {
    return ["Order", "Section", "Type", "Visible"].map((label) =>
      this.translate_label(label)
    );
  },
  combined_templates() {
    return build_combined_template_records(this);
  },
  total_count() {
    return this.combined_templates.length;
  },
  total_pages() {
    return Math.max(Math.ceil(this.total_count / this.page_size), 1);
  },
  next_page() {
    return this.page_number < this.total_pages ? this.page_number + 1 : null;
  },
  prev_page() {
    return this.page_number > 1 ? this.page_number - 1 : null;
  },
  paginated_templates() {
    return paginate_template_records(
      this.combined_templates,
      this.page_number,
      this.page_size
    );
  },
  table_safe_templates() {
    return this.paginated_templates.map((template_record) =>
      build_table_row(template_record)
    );
  },
  detail_entity_types() {
    return (this.selected_template_record?.entity_type_assignments || []).map(
      (assignment) => ({
        id: assignment.entity_type?.id,
        entity_type_name: assignment.entity_type?.entity_type_name || "-",
      })
    );
  },
  selected_template_record() {
    return this.selected_template?.template || null;
  },
  is_selected_pdf_template() {
    return this.selected_template?.template_kind === PDF_TEMPLATE_KIND;
  },
  detail_columns() {
    return (this.selected_template_record?.columns || []).map((column) => ({
      order: column.order_index,
      field: this.translate_label(column.report_field?.display_name || "-"),
      label: this.get_column_label(column),
      include_blank: column.include_if_blank ? "Yes" : "No",
    }));
  },
  detail_question_filters() {
    return (this.selected_template_record?.filters || [])
      .filter((filter_row) => filter_row.section_question)
      .map((filter_row) => ({
        filter_type: this.translate_label(
          filter_row.filter_type?.display_name || "-"
        ),
        question: filter_row.section_question?.question_text || "-",
        question_type: this.format_question_type(
          filter_row.section_question?.question_type
        ),
        form: filter_row.section_question?.form?.form_name || "-",
        section: filter_row.section_question?.section?.name || "-",
        value: this.format_filter_value(filter_row.filter_value),
      }));
  },
  detail_question_metrics() {
    return (this.selected_template_record?.question_metrics || []).map(
      (question_metric) => ({
        order: question_metric.order_index,
        metric: this.translate_label(
          question_metric.metric_type?.display_name || "-"
        ),
        question: question_metric.section_question?.question_text || "-",
        question_type: this.format_question_type(
          question_metric.section_question?.question_type
        ),
        form: question_metric.section_question?.form?.form_name || "-",
        section: question_metric.section_question?.section?.name || "-",
      })
    );
  },
  detail_other_filters() {
    return (this.selected_template_record?.filters || [])
      .filter((filter_row) => !filter_row.section_question)
      .map((filter_row) => ({
        filter_type: this.translate_label(
          filter_row.filter_type?.display_name || "-"
        ),
        form: filter_row.form?.form_name || "-",
        section: filter_row.section?.name || "-",
        compliance_rule: filter_row.compliance_rule?.name || "-",
        value: this.format_filter_value(filter_row.filter_value),
      }));
  },
  detail_pdf_sections() {
    return (this.selected_template_record?.sections || []).map((section) => ({
      order: section.order_index,
      section: section.title || "Untitled section",
      type: this.translate_label(this.format_pdf_section_type(section.section_type)),
      visible: section.is_visible ? "Yes" : "No",
    }));
  },
};
